
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {HostDeleteResponse} from './HostDeleteResponse';

/**
 * HostDeleteRequest struct
 */
class HostDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} hostId
   * @param {string=} hostName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), hostId = null, hostName = null) {
    super()
    super.copy(parent)
    this.HostId = hostId
    this.HostName = hostName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!HostDeleteRequest}
   * @param {!HostDeleteRequest} other Other struct
   * @returns {!HostDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.HostId != null) {
      this.HostId = new UUID(other.HostId)
    } else {
      this.HostId = null
    }
    if (other.HostName != null) {
      this.HostName = other.HostName
    } else {
      this.HostName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!HostDeleteRequest}
   * @returns {!HostDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new HostDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new HostDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!HostDeleteRequest}
   * @param {!HostDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof HostDeleteRequest)) {
      throw new TypeError('Instance of HostDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!HostDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      HostId: ((this.HostId != null) ? this.HostId.toString() : null),
      HostName: ((this.HostName != null) ? this.HostName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return HostDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!HostDeleteRequest} other Object value
   * @returns {!HostDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new HostDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!HostDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return HostDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!HostDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6047
  }
}

export { HostDeleteRequest };

/**
 * Fast Binary Encoding HostDeleteRequest field model
 */
class FieldModelHostDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.HostId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.HostName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.HostId.fbeOffset + this.HostId.fbeSize), buffer, this.HostId.fbeOffset + this.HostId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.HostId.fbeSize + this.HostName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.HostId.fbeExtra + this.HostName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelHostDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6047
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelHostDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelHostDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.HostId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.HostId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HostId.fbeSize

    if ((fbeCurrentSize + this.HostName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.HostName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HostName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelHostDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelHostDeleteRequest}
   * @param {!HostDeleteRequest} fbeValue Default value, defaults is new HostDeleteRequest()
   * @returns {!HostDeleteRequest} HostDeleteRequest value
   */
  get (fbeValue = new HostDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelHostDeleteRequest}
   * @param {!HostDeleteRequest} fbeValue HostDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.HostId.fbeSize) <= fbeStructSize) {
      fbeValue.HostId = this.HostId.get()
    } else {
      fbeValue.HostId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HostId.fbeSize

    if ((fbeCurrentSize + this.HostName.fbeSize) <= fbeStructSize) {
      fbeValue.HostName = this.HostName.get()
    } else {
      fbeValue.HostName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.HostName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelHostDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelHostDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelHostDeleteRequest}
   * @param {!HostDeleteRequest} fbeValue HostDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelHostDeleteRequest}
   * @param {HostDeleteRequest} fbeValue HostDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.HostId.set(fbeValue.HostId)
    this.HostName.set(fbeValue.HostName)
  }
}

export { FieldModelHostDeleteRequest };

/**
 * Fast Binary Encoding HostDeleteRequest model
 */
class HostDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelHostDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the HostDeleteRequest model
   * @this {!HostDeleteRequestModel}
   * @returns {!FieldModelHostDeleteRequest} model HostDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!HostDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!HostDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return HostDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!HostDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelHostDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!HostDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!HostDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!HostDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!HostDeleteRequestModel}
   * @param {!HostDeleteRequest} value HostDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!HostDeleteRequestModel}
   * @param {!HostDeleteRequest} value HostDeleteRequest value, defaults is new HostDeleteRequest()
   * @return {!object} Deserialized HostDeleteRequest value and its size
   */
  deserialize (value = new HostDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new HostDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new HostDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!HostDeleteRequestModel}
   * @param {!number} prev Previous HostDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { HostDeleteRequestModel };
HostDeleteRequest.__has_response = true;
HostDeleteRequest.__response_class = HostDeleteResponse;
